// @givmo/org-console — the Profile page's user-visible copy, in ONE place (PE-WEB1).
//
// Every sentence a person reads on the page lives here, so a ruling on wording changes one constant
// and never a component. The words are class-generic on purpose: the host's vocabulary supplies the
// organization's own name; this package never learns what kind of organization it renders.
//
// ⚠ These strings are the MOCKUP's proposals (Profile Enrichment plan §5 PE-WEB1, M19: the
// moderation badge, the reason line, the unavailable state and the suppression banner are shown to
// Jonathan before the page ships). Change them here on the ruling; the tests read this file.

import { GLOBAL_ADMIN_LABEL, ROLE_OWNER, moduleTierLabel } from './org-tiers';

/** Who may change the page's content: the org tier and the module tier, both through the labellers
 *  (the vocabulary rule, R6 — "Owner" names a per-module tier, and reaches copy only through them). */
const CAN_CHANGE = `Only a ${GLOBAL_ADMIN_LABEL} or a module ${moduleTierLabel(ROLE_OWNER)} can change`;

/** A short calendar date for the reason line, locale-independent (`2026-09-05`). */
export function formatRemovedDate(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

export const PROFILE_COPY = {
  page: {
    title: 'Profile',
    subtitle: 'What people see about your organization on Givmo.',
  },
  tabs: {
    facts: 'Details',
    blocks: 'Page content',
    branding: 'Branding',
    collateral: 'Collateral',
    preview: 'View as public',
  },
  blocks: {
    heading: 'Page content',
    lede: 'Tell your story. Add up to 20 blocks — stories, photos and videos — plus up to 10 links. They appear on your public page in this order.',
    counters: (blocks: number, links: number): string =>
      `${blocks} of 20 blocks · ${links} of 10 links`,
    empty: 'Nothing on your page yet. Add a story, a photo, a video or a link.',
    add: {
      button: 'Add to your page',
      story: 'Story',
      image: 'Photo',
      video: 'Video',
      link: 'Link',
      full: 'Your page has all 20 blocks.',
      linksFull: 'Your page has all 10 links.',
    },
    kinds: {
      story: 'Story',
      image: 'Photo',
      video: 'Video',
      link: 'Link',
    },
    fields: {
      storyTitle: 'Title (optional)',
      storyBody: 'Your story',
      storyBodyHelper: 'Plain text, up to 5,000 characters.',
      imageUpload: 'Photo',
      imageUploadHint: 'Add photo',
      imageUploadHelper: 'PNG, JPG or WebP.',
      imageCaption: 'Caption (optional)',
      videoUrl: 'YouTube or Vimeo link',
      videoUrlHelper: 'Paste the video’s page link. It plays on your page without leaving Givmo.',
      videoCanonical: 'Plays from',
      linkTitle: 'Button text',
      linkUrl: 'Web address',
      linkUrlHelper: 'Must start with https://',
    },
    row: {
      moveUp: 'Move up',
      moveDown: 'Move down',
      remove: 'Remove',
      position: (index: number): string => `Block ${index + 1}`,
    },
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Your page is saved.',
    unsaved: 'You have unsaved changes.',
    /**
     * A save refused because the page changed after this draft was read (PE-WEB18; the server's
     * `profile_page_changed`, or PE-BE13's block that is no longer on the page). The iOS editor's
     * ruled words (D-PE-45), in the save bar, with Reload page in Save's place — ruled for the
     * console as drawn (D-PE-48, Jonathan 2026-10-14).
     */
    stale: {
      refused: 'Your page changed while you were editing, so your changes weren’t saved.',
      reloadKeeps: 'Reloading keeps them wherever it can.',
      reload: 'Reload page',
    },
    /**
     * What Reload page could not keep (D-PE-45's words), at the top of the blocks under the counters
     * until the next save, scrolled into view and read after each reload (D-PE-48 (1)).
     */
    reloaded: {
      title: 'We reloaded your page.',
      whileEditing: 'While you were editing:',
      everythingElse: 'Everything else you changed is still here — check it, then save.',
      removed: (name: string): string => `${name} was removed.`,
      removedWithEdits: (name: string): string =>
        `${name} was removed, so your edits to it weren’t kept.`,
      changed: (name: string): string => `${name} was changed, so your edits to it weren’t kept.`,
      back: (name: string): string => `${name} was changed, so it’s back on your page.`,
      order: 'Blocks were moved, so your order wasn’t kept.',
      /** How a line names a block: its kind and its own words, never its number (the reload moves it). */
      names: {
        story: (words: string | null): string => (words ? `The story “${words}”` : 'A story'),
        image: (caption: string | null): string => (caption ? `The photo “${caption}”` : 'A photo'),
        video: (provider: string | null): string =>
          provider ? `The ${provider} video` : 'A video',
        link: (text: string | null): string => (text ? `The link “${text}”` : 'A link'),
        other: 'A block',
      },
    },
    readOnly: `You can see your organization’s page content here. ${CAN_CHANGE} it.`,
    moderation: {
      badge: 'Removed by Givmo moderation',
      removedOn: (date: string | null): string => (date ? `Removed ${date}.` : 'Removed.'),
      reason: (reason: string): string => `Reason: ${reason}`,
      noReason: 'No reason was given.',
      stays:
        'It stays off your public page until Givmo restores it. You can’t edit it here, but you can remove it from your page.',
      contact: 'Questions? Email support@givmocharitable.org.',
    },
    unavailable: {
      title: 'Page content isn’t available for your organization yet.',
      body: 'Your public page shows your details today. When page content is switched on for your organization, you’ll add stories, photos, videos and links here.',
    },
    errors: {
      load: 'We couldn’t load your page content. Please try again.',
      save: 'We couldn’t save your page. Please try again.',
      storyBodyRequired: 'Write your story before saving.',
      storyBodyTooLong: 'A story can be up to 5,000 characters.',
      titleTooLong: 'A title can be up to 200 characters.',
      captionTooLong: 'A caption can be up to 200 characters.',
      imageRequired: 'Add a photo before saving.',
      videoRequired: 'Paste a YouTube or Vimeo link before saving.',
      videoHost: 'Videos must be a YouTube or Vimeo link.',
      linkTitleRequired: 'Give the link some button text.',
      linkTitleTooLong: 'Button text can be up to 100 characters.',
      linkUrlRequired: 'Add the web address the link goes to.',
      linkUrlHttps: 'Links must start with https://.',
      linkUrlTooLong: 'A web address can be up to 2,048 characters.',
    },
  },
  /**
   * The Statistics card (PE-WEB15) — Givmo's own figures under the page's header (D-PE-41). The words
   * for each stat (its label, what it counts, why it may be hidden) are the HOST'S; these are the
   * card's own.
   */
  statistics: {
    heading: 'Statistics',
    lede: 'These figures appear under the header of your public page. Givmo calculates them for this year so far. Choose which ones appear, and in what order.',
    notShown: 'Not on your page',
    allOff: 'No statistics appear on your page.',
    readOnly: `${CAN_CHANGE} which statistics appear.`,
    /** In a row's figure slot: the public doesn't see this figure (the line under the list says why). */
    hidden: 'Hidden',
    show: (label: string): string => `Show ${label}`,
    moveUp: (label: string): string => `Move ${label} up`,
    moveDown: (label: string): string => `Move ${label} down`,
    errors: {
      save: 'We couldn’t save which statistics appear. Please try again.',
      /**
       * A change refused because the Statistics choice changed after this page was read (PE-WEB18):
       * the card shows the latest choice at once, and this says why the change didn't stick. Ruled
       * as drawn (D-PE-48 (3), Jonathan 2026-10-14).
       */
      stale: 'Your statistics changed while you were editing, so your change wasn’t saved.',
    },
  },
  preview: {
    heading: 'View as public',
    lede: 'Your public page as people see it right now. Unsaved changes don’t appear here.',
    openPublic: 'Open public page',
    suppressed: {
      title: 'Your page content isn’t publicly visible right now',
      body: 'People can’t see your page content at the moment, so there is nothing to show. This usually means your organization’s public page is switched off. Questions? Email support@givmocharitable.org.',
    },
    unavailable: {
      title: 'Page content isn’t available for your organization yet.',
      body: 'Your public page shows your details today. There is no page content to view yet.',
    },
    empty: 'Your public page shows your details only — there is no page content yet.',
    playVideo: 'Play video',
    /** The eyebrow above the served blocks — the organization's own words, marked off from the host's. */
    attribution: (orgName: string) => `From ${orgName}`,
    loadError: 'We couldn’t load your public page. Please try again.',
  },
  /** Profile → Branding (EMC-WEB11; mockups 12a, 12b). */
  branding: {
    /** The lede's subject while the host has no name for the organization yet. */
    orgFallback: 'Your organization',
    /** The org's name is already in the sidebar and the header, so the lede doesn't repeat it. */
    lede: 'How you look and sound in your emails, posts and pages.',
    /** The two parts of Branding when the QR design is housed here: what each sub-tab holds. */
    sections: {
      label: 'Branding sections',
      brand: 'Logos, colors and words',
      qr: 'QR code design',
    },
    save: 'Save',
    saving: 'Saving…',
    saved: 'All changes saved',
    unsaved: 'Unsaved changes',
    saveFailed: 'Your changes weren’t saved. Please try again.',
    readOnly: `${CAN_CHANGE} your branding.`,
    /** A refused Save, by the field the server named. */
    fieldRefused: {
      colors: 'Check your colors: up to 12, each with a name of up to 40 characters.',
      fonts: 'Choose your fonts from the email-safe list.',
      tagline: 'Check the tagline: one line, up to 200 characters.',
      voice_notes: 'Check the voice and tone: up to 4,000 characters.',
    },
    logos: {
      title: 'Logos',
      primary: 'Primary',
      alternate: 'Alternate',
      dark: 'For dark backgrounds',
      replace: 'Replace',
      upload: 'Upload',
      uploading: 'Uploading…',
      empty: 'PNG or JPEG',
      /** An empty slot, to a person who may not fill it. */
      none: 'No logo yet',
      failed: 'That logo wasn’t uploaded. Please try again.',
    },
    colors: {
      title: 'Colors',
      add: 'Add color',
      empty: 'No colors yet. Add your colors and choose where each one is used.',
      edit: (name: string) => `Edit ${name}`,
      dialogAdd: 'Add a color',
      dialogEdit: 'Edit color',
      name: 'Name',
      value: 'Color',
      roles: 'Used for',
      rolesHelper: 'Each use belongs to one color.',
      movesFrom: (name: string) => `Moves from ${name}`,
      /** A colour this change leaves with no use at all. */
      leftUnused: (name: string) => `${name} will no longer be used for anything.`,
      remove: 'Remove color',
      cancel: 'Cancel',
      apply: 'Apply',
      /** Applying changes the page, not the saved brand: the page's Save keeps it. */
      applyNote: 'Apply shows it on the page. Then Save to keep it.',
      nameRequired: 'Give this color a name.',
      hexInvalid: 'Enter a color like #327DC0.',
    },
    fonts: {
      title: 'Fonts',
      subtitle: (fallback: string) =>
        `Email-safe, so every inbox shows them. Where one is missing, ${fallback} stands in.`,
      headings: 'Headings',
      body: 'Body',
      choose: 'Choose a font',
      headingsSample: 'Today is Giving Tuesday',
      bodySample: 'Thank you for giving today.',
    },
    words: {
      title: 'Words',
      subtitle: 'The assistant writes in this voice when it drafts an email or post.',
      tagline: 'Tagline',
      voice: 'Voice and tone',
      voicePlaceholder: 'How you write: warm or formal, the words you use and the ones you avoid.',
    },
    qr: {
      title: 'QR code design',
      lede: (orgName: string) =>
        `Every Givmo QR code for ${orgName} uses this design: your fundraisers and your public page.`,
    },
    unavailable: {
      title: 'Branding isn’t available for your organization yet.',
      body: 'When it is, your logos, colors, fonts and voice live here.',
    },
    loadError: 'We couldn’t load your branding. Please try again.',
  },
  /**
   * Why a logo or a library file cannot go up, in the server's own words (`OrgBrandService`'s
   * refusals), said before the upload starts (`brandUploadRefusal`).
   */
  uploadRefusals: {
    empty: 'That file is empty. Choose another.',
    logoType: 'A logo is a PNG or JPEG image.',
    collateralType: 'Add a PNG or JPEG image, or a PDF.',
    replaceKind:
      'Replace an image with an image, or a PDF with a PDF: everywhere this item is used expects the kind it is. Add the file as a new item instead.',
    imageTooLarge: 'An image can be up to 10 MB.',
    pdfTooLarge: 'A PDF can be up to 25 MB.',
  },
  /** Profile → Collateral (EMC-WEB11, EMC-BE14; mockup 12c). */
  collateral: {
    lede: 'Images and PDFs for your emails, posts and share kits. The assistant picks from here and skips anything you mark out of date.',
    /** What Add takes, beside the button. */
    addHelper: 'PNG or JPEG images up to 10 MB, PDFs up to 25 MB.',
    search: 'Search by name or tag',
    allTags: 'All tags',
    tagFilter: 'Tag',
    add: 'Add images or PDFs',
    uploading: 'Uploading…',
    uploadFailed: (fileName: string, reason: string) => `${fileName}: ${reason}`,
    uploadFailedReason: 'It wasn’t added. Please try again.',
    dismiss: 'Dismiss',
    outOfDate: 'Out of date',
    /** Under "Out of date" on its card: what it means, and the way back. */
    outOfDateNote: 'The assistant skips it.',
    putBack: 'Put back in use',
    dated: (date: string) => `Dated ${date}`,
    pdf: 'PDF',
    open: (title: string) => `Open ${title}`,
    actions: (title: string) => `Actions for ${title}`,
    menu: {
      replace: 'Replace',
      edit: 'Edit details',
      markOutOfDate: 'Mark out of date',
      markCurrent: 'Back in use',
      delete: 'Delete',
    },
    replaceFailed: 'The file wasn’t replaced. Please try again.',
    markFailed: 'That change wasn’t saved. Please try again.',
    edit: {
      title: 'Edit details',
      name: 'Title',
      description: 'Description',
      tags: 'Tags',
      tagsHelper: 'Separate tags with commas.',
      dated: 'Date it’s for (optional)',
      datedHelper:
        'Shown on the card. It doesn’t mark the item out of date: you do that from its menu.',
      cancel: 'Cancel',
      save: 'Save',
      failed: 'Your changes weren’t saved. Please try again.',
      titleRequired: 'Give this item a title.',
      tagTooLong: 'A tag can be up to 40 characters.',
      tooManyTags: 'An item can have up to 20 tags.',
      /** A refused Edit details, by the field the server named. */
      fieldRefused: {
        title: 'Check the title: one line, up to 120 characters.',
        description: 'Check the description: up to 500 characters.',
        tags: 'Check the tags: up to 20, each up to 40 characters.',
        dated_on: 'Check the date: it must fall between 1900 and 2100.',
      },
    },
    delete: {
      title: 'Delete this item?',
      body: 'It leaves your library for good. Emails that have gone out keep their copy.',
      cancel: 'Cancel',
      confirm: 'Delete',
      failed: 'It wasn’t deleted. Please try again.',
    },
    empty: {
      title: 'No graphics or photos yet',
      body: 'Add the graphics, photos and PDFs your emails, posts and share kits use.',
    },
    noMatch: 'Nothing in your library matches.',
    more: 'Show more',
    count: (shown: number, total: number) => `${shown} of ${total}`,
    unavailable: {
      title: 'Collateral isn’t available for your organization yet.',
      body: 'When it is, the graphics and photos your emails and posts use live here.',
    },
    loadError: 'We couldn’t load your collateral. Please try again.',
  },
  common: {
    retry: 'Try again',
    loading: 'Loading your page…',
  },
} as const;
